import { useEffect, useId, useLayoutEffect, useReducer, useRef } from 'react'
import type { HandleComponent, RndDragEvent } from 'react-rnd'
import { Rnd } from 'react-rnd'
import styled from 'styled-components'

import {
  annotationBounds,
  type DisplayBox,
  type DisplaySize,
  geometryFromDisplayBox,
  pointFromClient,
} from './markupEditorCoordinates'
import {
  rectangleHandleDescription,
  rectangleResizeHandles,
  resizeRectangleFromHandle,
} from './markupEditorHandles'
import { isPointInPage } from './markupEditorPolygon'
import {
  annotationGeometryEqual,
  geometryBounds,
  MIN_ANNOTATION_SIZE,
  translateGeometry,
} from './markupGeometry'
import { placeMarkupLabel } from './markupLabelPlacement'
import type { AnnotationGeometry, MarkupAnnotation, MarkupPoint } from './types'
import type { useMarkupEditorInteraction } from './useMarkupEditorInteraction'

/* eslint-disable no-classname/no-classname-outside-designsystem -- react-rnd needs its wrapper class as the drag boundary. */

type EditorInteraction = ReturnType<typeof useMarkupEditorInteraction>
const POINT_RADIUS = 6

const PointBounds = styled.div`
  position: absolute;
  inset: -${POINT_RADIUS}px;
  pointer-events: none;
`

const AnnotationSvg = styled.svg<{ $editable: boolean }>`
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
  outline: none;

  [data-annotation-shape='true'] {
    outline: none;
    pointer-events: auto;
    cursor: ${({ $editable }) => ($editable ? 'move' : 'pointer')};
  }

  [data-annotation-shape='true']:focus-visible {
    stroke-width: 4;
  }

  .annotation-shape-handle {
    touch-action: none;
  }

  [data-markup-label],
  [data-edit-vertex],
  [data-edge-index] {
    pointer-events: auto;
  }
`

const Label = styled.text`
  pointer-events: none;
`

const Vertex = styled.ellipse`
  cursor: move;
  fill: ${({ theme }) => theme.forecast.background1};
  stroke: ${({ theme }) => theme.forecast.primary};
  stroke-width: 2;
  vector-effect: non-scaling-stroke;

  &:focus-visible {
    stroke-width: 4;
  }
`

const safeId = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, '-')
const polygonPoints = (vertices: ReadonlyArray<MarkupPoint>) =>
  vertices.map(({ x, y }) => `${x},${y}`).join(' ')
export const labelPointFor = (geometry: AnnotationGeometry): MarkupPoint =>
  geometry.shape === 'point'
    ? geometry
    : geometry.shape === 'rectangle'
      ? { x: geometry.x_min, y: geometry.y_min }
      : geometry.vertices[0]!

export const MarkupLabel = ({
  label,
  placement,
  color,
  weight,
  annotationId,
}: {
  label: string
  placement: ReturnType<typeof placeMarkupLabel>
  color: string
  weight: number
  annotationId?: string
}) => (
  <Label
    aria-label={label}
    data-markup-label={annotationId ? true : undefined}
    data-annotation-id={annotationId}
    fill={color}
    fontSize={placement.fontSize}
    fontWeight={weight}
    paintOrder='stroke'
    stroke='white'
    strokeWidth={placement.strokeWidth}
    transform={`scale(${placement.textScaleX} 1)`}
    x={placement.x}
    y={placement.y}>
    <title>{label}</title>
    {placement.label}
  </Label>
)

const MarkTexture = ({
  annotation,
  active,
}: {
  annotation: MarkupAnnotation
  active: boolean
}) => {
  const texture = annotation.appearance.texture
  if (!texture) return null
  const id = `markup-pattern-${safeId(annotation.id)}-${safeId(texture.id ?? 'pattern')}`
  return (
    <defs>
      <pattern
        height={texture.step}
        id={id}
        patternUnits='userSpaceOnUse'
        width={texture.step}>
        {texture.directions.map((direction) => (
          <path
            d={
              direction === 'forward'
                ? `M-${texture.step},-${texture.step} L${texture.step * 2},${texture.step * 2}`
                : `M-${texture.step},${texture.step * 2} L${texture.step * 2},-${texture.step}`
            }
            fill='none'
            key={direction}
            stroke={annotation.appearance.color}
            strokeOpacity={active ? 0.9 : 0.6}
            strokeWidth={1.25}
            vectorEffect='non-scaling-stroke'
          />
        ))}
      </pattern>
    </defs>
  )
}

interface MarkupEditorAnnotationProps {
  annotation: MarkupAnnotation
  selected: boolean
  display: DisplaySize
  getPageBounds: () =>
    | Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>
    | undefined
  interaction: EditorInteraction
}

const resizedBox = (
  origin: DisplayBox,
  direction: string,
  delta: { width: number; height: number },
  display: DisplaySize,
  minimumSize = { width: MIN_ANNOTATION_SIZE, height: MIN_ANNOTATION_SIZE }
) => {
  const axis = direction.toLowerCase()
  if (delta.width === 0 && delta.height === 0) return origin
  const resize = (value: number, change: number, minimum: number) =>
    change === 0 ? value : Math.max(minimum, Math.round(value) + change)
  const width = resize(
    origin.width,
    delta.width,
    display.width * minimumSize.width
  )
  const height = resize(
    origin.height,
    delta.height,
    display.height * minimumSize.height
  )
  return {
    x: origin.x + (axis.endsWith('left') ? origin.width - width : 0),
    y: origin.y + (axis.startsWith('top') ? origin.height - height : 0),
    width,
    height,
  }
}

const eventPoint = (
  event: RndDragEvent | MouseEvent | TouchEvent,
  getPageBounds: MarkupEditorAnnotationProps['getPageBounds']
) => {
  let clientX: number | undefined
  let clientY: number | undefined
  if ('changedTouches' in event) {
    const touch = event.changedTouches[0] ?? event.touches[0]
    clientX = touch?.clientX
    clientY = touch?.clientY
  } else {
    clientX = event.clientX
    clientY = event.clientY
  }
  const bounds = getPageBounds()
  return clientX !== undefined && clientY !== undefined && bounds
    ? pointFromClient(clientX, clientY, bounds)
    : null
}

const displayBoxIsInsidePage = (
  box: DisplayBox,
  shape: AnnotationGeometry['shape'],
  display: DisplaySize
) => {
  if (shape === 'point') {
    const x = box.x + box.width / 2 - POINT_RADIUS
    const y = box.y + box.height / 2 - POINT_RADIUS
    return x >= 0 && y >= 0 && x <= display.width && y <= display.height
  }
  return (
    box.x >= 0 &&
    box.y >= 0 &&
    box.x + box.width <= display.width &&
    box.y + box.height <= display.height
  )
}

const ResizeHandle = styled.button<{ $color: string }>`
  color: ${({ $color }) => $color};
`

const handleButton = (
  annotationId: string,
  label: string,
  value: string,
  color: string
) => (
  <ResizeHandle
    key={`${annotationId}-${value}`}
    type='button'
    tabIndex={0}
    $color={color}
    data-markup-resize-handle
    aria-label={label}
    aria-description='Use the applicable arrow keys to adjust this handle.'
    aria-keyshortcuts='ArrowUp ArrowDown ArrowLeft ArrowRight'
    data-annotation-id={annotationId}
    data-edit-handle={value}
    lang='en'
  />
)

/** Owns the library transaction and the complete view for one annotation. */
export function MarkupEditorAnnotation({
  annotation,
  selected,
  display,
  getPageBounds,
  interaction,
}: MarkupEditorAnnotationProps) {
  const id = useId()
  const access = interaction.annotationAccess(annotation)
  const editingAllowed = selected && access.canEditControls
  const canResize = editingAllowed && annotation.geometry.shape === 'rectangle'
  const editableShape = access.canMove || canResize
  const color = annotation.appearance.color
  const active = selected || access.sameOwner
  const textureId = annotation.appearance.texture
    ? `markup-pattern-${safeId(annotation.id)}-${safeId(annotation.appearance.texture.id ?? 'pattern')}`
    : undefined
  const common = {
    fill: textureId ? `url(#${textureId})` : color,
    fillOpacity: textureId
      ? 1
      : active
        ? (annotation.appearance.selectedFillOpacity ??
          (annotation.geometry.shape === 'rectangle' ? 0.14 : 0.26))
        : (annotation.appearance.fillOpacity ?? 0.12),
    stroke: color,
    strokeDasharray: annotation.appearance.strokeDasharray,
    strokeWidth: (annotation.geometry.shape === 'rectangle' ? selected : active)
      ? 3
      : 2,
    vectorEffect: 'non-scaling-stroke' as const,
    'data-annotation-shape': 'true',
    'data-annotation-id': annotation.id,
    'aria-labelledby': `${id}-select ${id}-label`,
    'aria-describedby': editableShape ? `${id}-help` : undefined,
    'aria-pressed': selected,
    role: 'button' as const,
    tabIndex: 0,
    className: 'annotation-shape-handle',
  }
  const origin = annotationBounds(annotation.geometry, display, POINT_RADIUS)
  const geometrySignature = JSON.stringify(annotation.geometry)
  const constrainedRectangle = Boolean(
    interaction.rectangleConstraints &&
    annotation.geometry.shape === 'rectangle'
  )
  const [previewRevision, rerenderPreview] = useReducer(
    (revision: number) => revision + 1,
    0
  )
  const transaction = useRef<{
    geometry: AnnotationGeometry
    origin: DisplayBox
    key: string
    previewGeometry: AnnotationGeometry
    initialPointer?: MarkupPoint
    previousPointer?: MarkupPoint
  }>()
  const rnd = useRef<Rnd | null>(null)
  const cancelGesture = interaction.cancelGesture
  const currentGeometry = useRef(annotation.geometry)
  currentGeometry.current = annotation.geometry
  useEffect(() => {
    const started = transaction.current
    const replacedDuringGesture = Boolean(
      started &&
      started.key === interaction.key &&
      !annotationGeometryEqual(started.geometry, currentGeometry.current)
    )
    transaction.current = undefined
    if (started) rerenderPreview()
    if (replacedDuringGesture) cancelGesture({ cancelLibraryInteraction: true })
  }, [geometrySignature, interaction.key, constrainedRectangle, cancelGesture])
  const transactionIsCurrent = (
    started: NonNullable<typeof transaction.current>
  ) => {
    if (
      started.key === interaction.key &&
      annotationGeometryEqual(started.geometry, annotation.geometry)
    )
      return true
    transaction.current = undefined
    rerenderPreview()
    cancelGesture({ cancelLibraryInteraction: true })
    return false
  }
  const transactionPreview =
    transaction.current?.key === interaction.key &&
    annotationGeometryEqual(transaction.current.geometry, annotation.geometry)
      ? transaction.current.previewGeometry
      : undefined
  const geometry = transactionPreview ?? annotation.geometry
  const position = annotationBounds(geometry, display, POINT_RADIUS)
  const size = { width: position.width, height: position.height }
  useLayoutEffect(() => {
    const started = transaction.current
    if (
      !constrainedRectangle ||
      !started ||
      started.key !== interaction.key ||
      !annotationGeometryEqual(started.geometry, annotation.geometry)
    )
      return
    const nextBox = annotationBounds(
      started.previewGeometry,
      display,
      POINT_RADIUS
    )
    rnd.current?.updateSize({ width: nextBox.width, height: nextBox.height })
    // Draggable applies its raw position after onDrag, so sync after commit.
    rnd.current?.updatePosition({ x: nextBox.x, y: nextBox.y })
  }, [
    annotation.geometry,
    constrainedRectangle,
    display,
    interaction.key,
    previewRevision,
  ])
  const updatePreview = (
    started: NonNullable<typeof transaction.current>,
    next: DisplayBox
  ) => {
    const nextGeometry = geometryFromDisplayBox(
      started.geometry,
      started.origin,
      next,
      display
    )
    if (!nextGeometry) return
    started.previewGeometry = nextGeometry
    rerenderPreview()
  }
  const releasedInsidePage = (
    event: RndDragEvent | MouseEvent | TouchEvent
  ) => {
    const point = eventPoint(event, getPageBounds)
    return Boolean(point && isPointInPage(point))
  }

  const resizeHandleComponents: HandleComponent | undefined = canResize
    ? Object.fromEntries(
        rectangleResizeHandles.map(([handle, target, value]) => [
          handle,
          handleButton(
            annotation.id,
            `Resize annotation from ${rectangleHandleDescription(target, value)}`,
            value,
            color
          ),
        ])
      )
    : undefined
  const start = (event: RndDragEvent | MouseEvent | TouchEvent) => {
    const pointer = eventPoint(event, getPageBounds)
    transaction.current = {
      geometry: annotation.geometry,
      origin,
      key: interaction.key,
      previewGeometry: annotation.geometry,
      initialPointer: pointer ?? undefined,
      previousPointer: pointer ?? undefined,
    }
  }
  const updateConstrainedPreview = (
    event: RndDragEvent | MouseEvent | TouchEvent,
    direction?: string
  ) => {
    const started = transaction.current
    if (!started || !transactionIsCurrent(started) || !constrainedRectangle)
      return
    const pointer = eventPoint(event, getPageBounds)
    if (!pointer) return
    const referencePointer = direction
      ? started.initialPointer
      : started.previousPointer
    const isPointerUnchanged =
      pointer.x === started.previousPointer?.x &&
      pointer.y === started.previousPointer?.y
    started.previousPointer = pointer
    if (!referencePointer) return
    if (
      started.geometry.shape !== 'rectangle' ||
      started.previewGeometry.shape !== 'rectangle'
    )
      return
    const delta = {
      x: pointer.x - referencePointer.x,
      y: pointer.y - referencePointer.y,
    }
    const candidate = direction
      ? resizeRectangleFromHandle(
          started.geometry,
          rectangleResizeHandles.find(
            ([handle]) => handle === direction
          )?.[2] ?? direction,
          delta.x,
          delta.y,
          interaction.minimumRectangleSize,
          true
        )
      : translateGeometry(started.previewGeometry, { x: 0, y: 0 }, delta, true)
    if (!candidate || candidate.shape !== 'rectangle') return
    const next = isPointerUnchanged
      ? started.previewGeometry
      : interaction.constrainRectangleTransition(
          started.previewGeometry,
          candidate
        )
    if (!next) return
    started.previewGeometry = next
    rerenderPreview()
  }
  const finish = (
    event: RndDragEvent | MouseEvent | TouchEvent,
    next?: DisplayBox,
    direction?: string
  ) => {
    const started = transaction.current
    if (started && !transactionIsCurrent(started)) return
    if (!started) return
    let nextGeometry: AnnotationGeometry | undefined
    if (constrainedRectangle) {
      updateConstrainedPreview(event, direction)
      nextGeometry = started.previewGeometry
    } else if (
      next &&
      displayBoxIsInsidePage(next, started.geometry.shape, display) &&
      releasedInsidePage(event)
    ) {
      nextGeometry =
        geometryFromDisplayBox(
          started.geometry,
          started.origin,
          next,
          display
        ) ?? undefined
    }
    transaction.current = undefined
    rerenderPreview()
    if (nextGeometry)
      interaction.updateGeometry(
        annotation.id,
        started.geometry,
        nextGeometry,
        started.key
      )
  }
  const pointShape = geometry.shape === 'point'
  const bounds = geometryBounds(geometry)
  const viewBox = pointShape
    ? {
        x_min: geometry.x - POINT_RADIUS / display.width,
        y_min: geometry.y - POINT_RADIUS / display.height,
        x_max: geometry.x + POINT_RADIUS / display.width,
        y_max: geometry.y + POINT_RADIUS / display.height,
      }
    : bounds
  const placement = placeMarkupLabel(
    labelPointFor(geometry),
    annotation.label,
    display,
    'annotation',
    bounds
  )

  const editor = (
    <Rnd
      key={`${annotation.id}-${interaction.key}`}
      ref={rnd}
      bounds='parent'
      cancel='[data-markup-label], [data-edit-vertex], [data-edge-index], [data-markup-resize-handle]'
      className='markup-rnd-annotation'
      dragHandleClassName='annotation-shape-handle'
      disableDragging={!access.canMove}
      enableResizing={canResize}
      minWidth={
        annotation.geometry.shape === 'rectangle'
          ? display.width * interaction.minimumRectangleSize.width
          : undefined
      }
      minHeight={
        annotation.geometry.shape === 'rectangle'
          ? display.height * interaction.minimumRectangleSize.height
          : undefined
      }
      position={{ x: position.x, y: position.y }}
      resizeHandleComponent={resizeHandleComponents}
      resizeHandleWrapperClass='markup-resize-handle-wrapper'
      size={size}
      onDrag={(event, data) => {
        const started = transaction.current
        if (!started || !transactionIsCurrent(started)) return
        if (constrainedRectangle) {
          updateConstrainedPreview(event)
          return
        }
        const point = started.geometry.shape === 'point'
        const x = point ? Math.min(display.width, Math.max(0, data.x)) : data.x
        const y = point ? Math.min(display.height, Math.max(0, data.y)) : data.y
        updatePreview(started, { ...started.origin, x, y })
      }}
      onDragStart={start}
      onDragStop={(event, data) => {
        const started = transaction.current
        finish(event, started && { ...started.origin, x: data.x, y: data.y })
      }}
      onResize={(event, direction, _element, delta) => {
        const started = transaction.current
        if (!started || !transactionIsCurrent(started)) return
        if (constrainedRectangle) updateConstrainedPreview(event, direction)
        else
          updatePreview(
            started,
            resizedBox(started.origin, direction, delta, display)
          )
      }}
      onResizeStart={start}
      onResizeStop={(event, direction, _element, delta) => {
        const started = transaction.current
        finish(
          event,
          started &&
            resizedBox(
              started.origin,
              direction,
              delta,
              display,
              interaction.minimumRectangleSize
            ),
          direction
        )
      }}>
      <AnnotationSvg
        aria-label={`Annotation ${annotation.label}`}
        aria-current={selected ? 'true' : undefined}
        data-annotation-id={annotation.id}
        preserveAspectRatio='none'
        role='group'
        viewBox={`${viewBox.x_min} ${viewBox.y_min} ${viewBox.x_max - viewBox.x_min} ${viewBox.y_max - viewBox.y_min}`}
        $editable={editableShape}>
        <title id={`${id}-select`} lang='en'>
          Select annotation
        </title>
        <title id={`${id}-label`}>{annotation.label}</title>
        {editableShape && (
          <desc id={`${id}-help`} lang='en'>
            Use arrow keys to move this annotation by 1% of the page; hold Shift
            to move by 5%.
          </desc>
        )}
        <MarkTexture active={active} annotation={annotation} />
        <MarkupLabel
          label={annotation.label}
          placement={placement}
          color={color}
          weight={700}
          annotationId={annotation.id}
        />
        {pointShape && (
          <ellipse
            {...common}
            cx={geometry.x}
            cy={geometry.y}
            rx={POINT_RADIUS / display.width}
            ry={POINT_RADIUS / display.height}
          />
        )}
        {geometry.shape === 'rectangle' && (
          <rect
            {...common}
            x={geometry.x_min}
            y={geometry.y_min}
            width={geometry.x_max - geometry.x_min}
            height={geometry.y_max - geometry.y_min}
          />
        )}
        {geometry.shape === 'polygon' && (
          <>
            <polygon {...common} points={polygonPoints(geometry.vertices)} />
            {selected &&
              geometry.vertices.map((vertex, vertexIndex) => {
                const next =
                  geometry.vertices[
                    (vertexIndex + 1) % geometry.vertices.length
                  ]!
                const activeVertex = interaction.selectedVertex === vertexIndex
                const edgeLabel = `Insert an annotation vertex between vertices ${vertexIndex + 1} and ${((vertexIndex + 1) % geometry.vertices.length) + 1}`
                return (
                  <g key={`${annotation.id}-vertex-${vertexIndex}`}>
                    <line
                      aria-label={edgeLabel}
                      aria-disabled={!editingAllowed}
                      data-edge-index={vertexIndex}
                      data-annotation-id={annotation.id}
                      lang='en'
                      role='button'
                      stroke='transparent'
                      strokeWidth={12}
                      tabIndex={editingAllowed ? 0 : -1}
                      vectorEffect='non-scaling-stroke'
                      x1={vertex.x}
                      x2={next.x}
                      y1={vertex.y}
                      y2={next.y}
                      onClick={(event) =>
                        interaction.insertVertex(
                          annotation,
                          vertexIndex,
                          { x: event.clientX, y: event.clientY },
                          'client'
                        )
                      }
                    />
                    <Vertex
                      aria-label={`Move annotation vertex ${vertexIndex + 1}`}
                      aria-disabled={!editingAllowed}
                      aria-keyshortcuts='ArrowUp ArrowDown ArrowLeft ArrowRight Delete'
                      data-edit-vertex={vertexIndex}
                      data-annotation-id={annotation.id}
                      lang='en'
                      role='button'
                      tabIndex={editingAllowed ? 0 : -1}
                      cx={vertex.x}
                      cy={vertex.y}
                      rx={(8 / display.width) * (activeVertex ? 1.25 : 1)}
                      ry={(8 / display.height) * (activeVertex ? 1.25 : 1)}
                      onPointerDown={(event) =>
                        interaction.handleVertexPointerDown(
                          event,
                          annotation,
                          vertexIndex
                        )
                      }
                    />
                  </g>
                )
              })}
          </>
        )}
      </AnnotationSvg>
    </Rnd>
  )
  return pointShape ? (
    <PointBounds className='markup-point-bounds'>{editor}</PointBounds>
  ) : (
    editor
  )
}
